import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { ColumnType } from "@/Utils/tableColumns/functions";
import useSorting from "@/Utils/useSorting";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import { EmptyDataType } from "@/components/EmptyView/emptyViewTypes";
import TableHeader from "@/components/TableHeader";
import { useTrans } from "@/translations";

import React from "react";

import { noop, orderBy } from "lodash";
import { twMerge } from "tailwind-merge";

import { TableRow } from "./TableRow";

type Props = {
  columns: ColumnType[];
  data: { [k: string]: any }[];
  isCollapsible?: boolean;
  getExpandedRowView?: (values: { [k: string]: any }) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  rowClassName?: string;
  sortable?: boolean;
  sortingHeader?: string;
  rightAlign?: boolean;
  emptyViewType?: EmptyDataType;
  emptyViewClassName?: string;
  showHeadersWhenEmpty?: boolean;
  hasDelete?: boolean;
  hideDeleteSingle?: boolean;
  limitedHeight?: boolean;
  deleteFunction?: (id: string) => void;
  showPreview?: boolean;
  previewHintKey?: string;
  isSmallScreen?: boolean;
  tableClassName?: string;
  isLoading?: boolean;
};

export const Table: React.FC<Props> = ({
  data,
  columns,
  isCollapsible = false,
  getExpandedRowView,
  className = "",
  headerClassName = "",
  rowClassName = "",
  sortable = true,
  sortingHeader = "",
  rightAlign = false,
  emptyViewType,
  emptyViewClassName = "",
  showHeadersWhenEmpty = true,
  hasDelete = false,
  limitedHeight,
  deleteFunction = noop,
  showPreview = false,
  previewHintKey = "",
  isSmallScreen = false,
  tableClassName = "",
  isLoading = false,
}) => {
  const { t } = useTrans();
  const { field, direction, changeSorting } = useSorting(sortingHeader);

  const sortedData = orderBy(data, field, direction);

  return (
    <div className={twMerge("mb-2.5 text-end", className)}>
      <div className={twMerge(isSmallScreen && "max-w-full overflow-x-auto")}>
        {(sortedData?.length > 0 || showHeadersWhenEmpty) && (
          <div
            className={twMerge(
              `top-0 z-10 grid min-h-[48px] bg-white grid-cols-${columns.length} border-grey3 border-b py-1`,
              hasDelete && "pr-10",
              isSmallScreen && "min-w-[900px]",
              headerClassName,
            )}
          >
            {columns.map((h, i) => {
              const isColumnSortable =
                h.sortable != null ? h.sortable : sortable;
              return (
                <TableHeader
                  key={`${h.name}-${i}`}
                  label={h.label}
                  field={h.name}
                  setSort={isColumnSortable ? changeSorting : undefined}
                  activeField={field}
                  direction={direction}
                  tooltipText={h.headerTooltipText}
                  tooltipComponent={h.headerTooltipComponent}
                  className={twMerge(
                    rightAlign && "justify-end pr-3 text-end",
                    i === 0 && `justify-start text-start`,
                    h.headerClassNames,
                  )}
                />
              );
            })}
          </div>
        )}

        <div
          className={twMerge(
            isSmallScreen && "min-w-[900px]",
            limitedHeight && "max-h-[240px] overflow-y-auto",
            tableClassName,
          )}
        >
          {isLoading ? (
            <div className="mt-5 flex h-full min-h-full items-center justify-center">
              <LoadingIndicator />
            </div>
          ) : sortedData?.length > 0 ? (
            sortedData.map((values, i) => (
              <TableRow
                key={`${values.name}-${i}`}
                values={values}
                columns={columns}
                isCollapsible={isCollapsible}
                getExpandedRowView={getExpandedRowView}
                rowClassName={rowClassName}
                hasDelete={hasDelete}
                deleteFunction={deleteFunction}
                isLast={i === sortedData.length - 1}
              />
            ))
          ) : (
            emptyViewType && (
              <EmptyView
                type={emptyViewType}
                className={twMerge("mt-5 min-h-[75px]", emptyViewClassName)}
              />
            )
          )}
        </div>

        {sortedData?.length > 0 && showPreview && (
          <div className="align-center border-grey3 text-grey6 flex justify-center border-y border-t py-4 text-center text-sm whitespace-pre-wrap">
            {t(previewHintKey)}
          </div>
        )}
      </div>
    </div>
  );
};
